import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { Campaign } from '@/api/worldCup';
import { clearAuth, useAuth } from '@/features/auth/authStore';
import { ReportSwitcher } from './ReportSwitcher';
import styles from './Header.module.css';

/**
 * `currentReportId` is the report being viewed — drives the report switcher's
 * selected value and tells it which option to omit-as-current. Auth is unified
 * (one token), so user identity comes straight from `useAuth()`.
 */
export function Header({ campaign, currentReportId }: { campaign: Campaign; currentReportId: string }) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  function onSignOut() {
    clearAuth();
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  return (
    <header className="app-header">
      <div className="brand-lockup">
        <div className="brand-chip" aria-hidden>
          C
        </div>
        <div>
          <div className="brand-title">{campaign.title}</div>
          {campaign.subtitle && <div className="brand-sub">{campaign.subtitle}</div>}
        </div>
      </div>

      <div className={styles.account}>
        <ReportSwitcher allowedReports={auth?.allowedReports ?? []} currentReportId={currentReportId} isAdmin={auth?.role === 'admin'} />
        {auth?.role === 'admin' && (
          <>
            <button type="button" className={styles.adminLink} onClick={() => navigate('/admin/users')}>
              Manage users
            </button>
            <button type="button" className={styles.adminLink} onClick={() => navigate('/admin/runs')}>
              Report runs
            </button>
            <button type="button" className={styles.adminLink} onClick={() => navigate('/admin/config')}>
              Settings
            </button>
          </>
        )}
        {auth?.clientId && (
          <div className={styles.meta}>
            <span className={styles.client}>{auth.clientId}</span>
            {auth.role && <span className={styles.role}>· {auth.role}</span>}
          </div>
        )}
        <button type="button" className={styles.signOut} onClick={onSignOut}>
          Sign out
        </button>
      </div>
    </header>
  );
}
